import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Zap, Image, Video, ArrowRight, TrendingUp, Clock, Sparkles } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [recentGens, setRecentGens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [u, g] = await Promise.all([api.getUsage(), api.listGenerations({ limit: 6 })]);
        setUsage(u);
        setRecentGens(g);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  const usedPct = usage ? Math.round((usage.generations_used / Math.max(usage.generations_limit, 1)) * 100) : 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-8 animate-fade-in-up opacity-0" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>
          Welcome back, <span className="text-[#CDFE65]">{user?.name || 'Creator'}</span>
        </h1>
        <p className="text-white/40 mt-1">Here's your creative studio overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="stats-grid">
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-mono uppercase tracking-wider text-white/40">Usage</span>
            <Zap className="w-4 h-4 text-[#CDFE65]" />
          </div>
          <div className="text-2xl font-black" style={{ fontFamily: 'Chivo' }}>{usage?.generations_used || 0}<span className="text-sm text-white/30 font-normal">/{usage?.generations_limit || 10}</span></div>
          <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-[#CDFE65] rounded-full transition-all duration-500" style={{ width: `${usedPct}%` }} />
          </div>
        </div>
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-mono uppercase tracking-wider text-white/40">Remaining</span>
            <TrendingUp className="w-4 h-4 text-[#06B6D4]" />
          </div>
          <div className="text-2xl font-black" style={{ fontFamily: 'Chivo' }}>{usage?.remaining || 0}</div>
          <p className="text-xs text-white/30 mt-1">generations left</p>
        </div>
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-mono uppercase tracking-wider text-white/40">This Month</span>
            <Clock className="w-4 h-4 text-[#7C3AED]" />
          </div>
          <div className="text-2xl font-black" style={{ fontFamily: 'Chivo' }}>{usage?.generations_this_month || 0}</div>
          <p className="text-xs text-white/30 mt-1">generations</p>
        </div>
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-mono uppercase tracking-wider text-white/40">Plan</span>
            <Sparkles className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div className="text-2xl font-black capitalize" style={{ fontFamily: 'Chivo' }}>{usage?.plan || 'free'}</div>
          <Link to="/billing" className="text-xs text-[#CDFE65] hover:underline mt-1 inline-block" data-testid="upgrade-link">Upgrade</Link>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="quick-actions">
        <Link to="/studio" className="bg-[#0F0F11] border border-white/5 rounded-xl p-6 card-hover group flex items-center gap-4" data-testid="quick-action-studio">
          <div className="w-12 h-12 rounded-xl bg-[#CDFE65]/10 flex items-center justify-center shrink-0">
            <Video className="w-6 h-6 text-[#CDFE65]" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold mb-0.5">Generate Content</h3>
            <p className="text-sm text-white/40">Create videos and images with AI</p>
          </div>
          <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-[#CDFE65] transition-colors" />
        </Link>
        <Link to="/brand-kits" className="bg-[#0F0F11] border border-white/5 rounded-xl p-6 card-hover group flex items-center gap-4" data-testid="quick-action-brandkits">
          <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
            <Image className="w-6 h-6 text-[#7C3AED]" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold mb-0.5">Brand Kits</h3>
            <p className="text-sm text-white/40">Manage your brand identity</p>
          </div>
          <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-[#7C3AED] transition-colors" />
        </Link>
      </div>

      {/* Recent Generations */}
      <div data-testid="recent-generations">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo' }}>Recent Generations</h2>
          <Link to="/gallery" className="text-sm text-[#CDFE65] hover:underline" data-testid="view-all-link">View all</Link>
        </div>
        {recentGens.length === 0 ? (
          <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-12 text-center">
            <Sparkles className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/40 mb-4">No generations yet. Start creating!</p>
            <Link to="/studio" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="empty-state-studio-btn">
              Open Studio <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {recentGens.map((gen) => (
              <div key={gen.id} className="relative group overflow-hidden rounded-lg border border-white/5 card-hover" data-testid={`gen-card-${gen.id}`}>
                {gen.output_url ? (
                  <img src={gen.output_url} alt={gen.prompt || 'Generation'} className="w-full aspect-square object-cover" loading="lazy" />
                ) : (
                  <div className="w-full aspect-square bg-[#18181B] flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${gen.status === 'completed' ? 'bg-[#CDFE65]/20 text-[#CDFE65]' : gen.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {gen.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
